/* eslint-disable no-underscore-dangle */
import { UrlService } from '@sm360/widget-commons-core-services';

// TODO: Some functions can be reused in the main provider
// we need to check witch functions that can be move...
class FilterRangeService {
    /**
     * _getIntegerOrNull - function that return an integer if is a valid integer or null if not.
     *
     * @param  {Number} number The number to check.
     * @return {Number} An valid integer number or null
     */
    _getIntegerOrNull(number) {
        if (Number.isInteger(number)) {
            return number;
        }
        return null;
    }

    /**
     * _getIntegerFromStr - function that convert a string to an integer.
     *
     * @param  {String} str The string to convert to integer
     * @return {Number} The Integer number
     */
    _getIntegerFromStr(str) {
        const number = Number.parseInt(str, 10);
        return this._getIntegerOrNull(number);
    }

    /**
     * _getUrlParamValue - function that convert a parameter url into an valid Integer.
     * If the url parameter value is not an integer return null.
     *
     * @param  {String|Array} paramNames The parameter name(s) of the URL
     * @return {Number} The value of the URL parameter name (integer).
     */
    _getUrlParamValue(paramNames) {
        const paramValue = UrlService.getFirstUrlParameter(paramNames);
        if (paramValue) {
            return this._getIntegerFromStr(paramValue);
        }
        return null;
    }

    /**
     * _getRoundMin - function that return a rounded whole number. The Number
     * is between [0, value*step]
     *
     * @param  {Number} value The value to round
     * @param  {Number} step  The step to divide
     * @return {Number} Number between [0, value*step]
     */
    _getRoundMin(value, step) {
        return Math.floor(value / step) * step;
    }

    /**
     * _getRoundMax - function that return a rounded whole number. The Number
     * is between [step, value*step] except if value is 0 then the Number is 0 too.
     *
     * @param  {Number} value The value to round
     * @param  {Number} step  The step to divide
     * @return {Number} number between [step, value*step]
     */

    _getRoundMax(value, step) {
        return Math.ceil(value / step) * step;
    }

    /**
     * getUrlValues - function that read the parameter value of the URL of minimum and maximum.
     * The alternative parameters are used only if the primary parameters have no associated values.
     *
     * @param  {String|Array} urlParameterMin The URL parameter name minimum. If it is an array, the first value found with be used.
     * @param  {String|Array} urlParameterMax The URL parameter name maximum. If it is an array, the first value found with be used.
     * @return {Object} The object that contains the value of {urlMin, urlMax}
     */
    getUrlValues(urlParameterMin, urlParameterMax) {
        return {
            urlMin: this._getUrlParamValue(urlParameterMin),
            urlMax: this._getUrlParamValue(urlParameterMax),
        };
    }

    /**
     * getNumbers - function that parse an array of metaData that contain label.
     * It convert each metaData label from the array into an integer array.
     *
     * @param  {Record} metaDatas Record Immutable that contain a label parameter
     * @return {Array} Array of Integer of the corresponding value of the label Records array.
     */
    getNumbers(metaDatas) {
        return metaDatas
            .map((metaData) => {
                const label = metaData.get('label');
                return this._getIntegerFromStr(label);
            })
            .filter((number) => number != null);
    }

    /**
     * getExtremeValues - Function that return the extreme values from the metaDatas array.
     * If the metaDatas is empty it return null value of extremes if the metaData array contain two or more
     * values, it return the minimum value and the maximum value of the array. If only one value,
     * it return the same value for the minimum and the maximum. Or null for extremeMin and roundExtremeMax
     * if no metaDatas.
     *
     * @param  {Array} metaDatas Array of metaDatas to get the extreme values
     * @return {Object} Object that contain {extremeMin, extremeMax}
     */
    getExtremeValues(metaDatas) {
        const NULL_EXTREME_VALUES = {
            extremeMin: null,
            extremeMax: null,
        };

        if (!metaDatas || metaDatas.length === 0) {
            return NULL_EXTREME_VALUES;
        }

        const values = this.getNumbers(metaDatas);
        if (values.length >= 2) {
            return {
                extremeMin: this._getIntegerOrNull(Math.min(...values)),
                extremeMax: this._getIntegerOrNull(Math.max(...values)),
            };
        }

        if (values.length === 1) {
            const value = values[0] || null;
            return {
                extremeMin: value,
                extremeMax: value,
            };
        }

        return NULL_EXTREME_VALUES;
    }

    /**
     * getInitState - This function return a initial state of filterRange.
     * extreme values = database or default if no database.
     * selected values = URL or extreme values if no URL
     * All the number is rounded with the corresponding extreme.
     *
     * @param  {Object} parameters Object that contain :
     * metadataCategorie : (Array) The array of metaDatas
     * URL_PARAMETER_MIN : (String) The name of the URL minimum parameter
     * URL_PARAMETER_MAX : (String) The name of the URL maximum parameter
     * DEFAULT_MIN : (Integer) The default minimum if metadataCategorie is empty and no url parameter.
     * DEFAULT_MAX : (Integer) The default minimum if metadataCategorie is empty and no url parameter.
     * DEFAULT_STEP : (Integer) The default step of the filterRange.
     *
     * @return {Object} Object that contain :
     * selectedMin : (Integer) The initialisation of the minimum of the filterRange
     * selectedMax : (Integer) The initialisation of the maximum of the filterRange
     * extremeMin : (Integer) The extreme minimum of the filterRange
     * extremeMax : (Integer) The extreme maximum of the filterRange
     *
     */
    getInitState(parameters) {
        const {
            metadataCategorie,
            URL_PARAMETER_MIN,
            URL_PARAMETER_MAX,
            DEFAULT_MIN,
            DEFAULT_MAX,
            DEFAULT_STEP,
            contextYear,
        } = parameters;

        let { urlMin, urlMax } = this.getUrlValues(URL_PARAMETER_MIN, URL_PARAMETER_MAX);
        if (contextYear && UrlService.containsPath(contextYear)) {
            urlMin = contextYear;
            urlMax = contextYear;
        }

        const extremeValues = this.getExtremeValues(metadataCategorie);
        const extremeMin = extremeValues.extremeMin || DEFAULT_MIN;
        const extremeMax = extremeValues.extremeMax || DEFAULT_MAX;

        const selectedMin = urlMin || extremeMin;
        const selectedMax = urlMax || extremeMax;

        const roundSelectedMin = this._getRoundMin(selectedMin, DEFAULT_STEP);
        const roundSelectedMax = this._getRoundMax(selectedMax, DEFAULT_STEP);

        const roundExtremeMin = this._getRoundMin(extremeMin, DEFAULT_STEP);
        const roundExtremeMax = this._getRoundMax(extremeMax, DEFAULT_STEP);

        return {
            selectedMin: roundSelectedMin,
            selectedMax: roundSelectedMax,
            extremeMin: roundExtremeMin,
            extremeMax: roundExtremeMax,
        };
    }
}

export default new FilterRangeService();
